import { parse, stringify } from 'fast-querystring';

/**
 * Code archaeology notes here:
 *
 * In 7/2024, we introduced this library as a way to effect a library upgrade:
 *
 * Previously we were doing parse() and stringify() operations for query-string-like things
 * using an antiquated version of the query-string library; this package instead uses fast-querystring.
 * fast-querystring implementation has three undesirable behaviors, though:
 *
 * the parse() function will not automatically disregard any leading ? character in the query argument
 * the parse() function returns a Record<string, any> rather than something Dictionary<string> based
 *   with the possibility of additional type specification
 * the stringify() function will still output something for a null or undefined key-value value.
 *
 * and one behavior that was not technically wrong but assumptions in our code crept in:
 *
 * the stringify() function will output its key/value pairs in the received iteration order. query-string did not do
 * exactly this; it would alphabetize the keys first.
 *
 * So rather than using the underlying library functionality directly from product code, we call into these
 * thin wrappers instead.
 *
 * Still TODO(matthew-tock): some unit tests
 */

/**
 * This was previously defined in shared/typings/index.d.ts
 *
 * Feels like it should maybe be moved to coretypes? But here for now is okay.
 */
export interface Dictionary<T> {
  [key: string]: T;
}

function wrappedParse<P = {}>(arg: string): P & Dictionary<string> {
  if (arg.startsWith('?')) {
    arg = arg.substring(1);
  }
  return parse(arg) as P & Dictionary<string>;
}

export { wrappedParse as parse };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function wrappedStringify(values: Record<string, any>) {
  const entries = Object.entries(values).filter(([_, val]) => val != null);
  entries.sort(([aKey, _aValUnused], [bKey, _bValUnused]) => aKey.localeCompare(bKey));

  const filteredValues = Object.fromEntries(entries);

  return stringify(filteredValues);
}

export { wrappedStringify as stringify };

export default {
  stringify: wrappedStringify,
  parse: wrappedParse,
};
